import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { cn } from "@/utils/cn.ts";
import { cardSurfaceClass } from "@/components/ui/card.tsx";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, TriangleAlert } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { DeviceCodeEntry } from "@/components/auth/device-approval/code-entry.tsx";
import type { DeviceApprovalViewProps } from "@/components/auth/device-approval/types.ts";
import { DeviceApprovalCard } from "@/components/auth/device-approval/approval-card.tsx";
import { MotionLoader } from "@/components/custom/motion-loader.tsx";
import { Button } from "@/components/ui/button.tsx";
import { m } from "@/paraglide/messages.js";
import {
	deviceVerifyQueryOptions,
	useDeviceApproveMutation,
} from "@/services/device-service.ts";
import { normalizeUserCode } from "@/shared/device-code.ts";
import { pageTitle } from "@/utils/page-title.ts";

export const Route = createFileRoute("/device")({
	validateSearch: z.object({
		code: z.string().optional(),
	}),
	beforeLoad: ({ context, location }) => {
		if (!context.auth.isAuthenticated) {
			throw redirect({
				to: "/auth/login",
				search: { redirect: location.href },
			});
		}
	},
	head: () => ({ meta: [{ title: pageTitle(m.auth_device_page_title()) }] }),
	component: DeviceApprovalPage,
});

function Shell({ children }: { children: React.ReactNode }) {
	return (
		<main className="relative min-h-screen overflow-hidden bg-background text-foreground">
			<div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,hsl(var(--primary)/0.12)_0%,transparent_60%)]" />
			<div className="relative z-10">{children}</div>
		</main>
	);
}

function CenteredCard({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex min-h-screen items-center justify-center px-4">
			<div
				className={cn(
					cardSurfaceClass,
					"w-full max-w-md rounded-2xl p-8 text-center",
				)}
			>
				{children}
			</div>
		</div>
	);
}

function DeviceApprovalPage() {
	const { code: urlCode } = Route.useSearch();
	const navigate = useNavigate();
	const [typedCode, setTypedCode] = useState<string | undefined>();
	const [approved, setApproved] = useState(false);

	const rawCode = urlCode ?? typedCode;
	const canonicalCode = rawCode ? normalizeUserCode(rawCode) : undefined;

	const verify = useQuery({
		...deviceVerifyQueryOptions(canonicalCode ?? ""),
		enabled: canonicalCode !== undefined,
		retry: false,
	});
	const approveMutation = useDeviceApproveMutation();

	const onCancel = () => navigate({ to: "/home" });

	if (canonicalCode === undefined) {
		return (
			<Shell>
				<DeviceCodeEntry onSubmit={setTypedCode} />
			</Shell>
		);
	}

	if (approved) {
		return (
			<Shell>
				<CenteredCard>
					<div className="mb-6 inline-flex size-16 items-center justify-center rounded-2xl bg-green-500/10 text-green-600 ring-1 ring-green-500/20 dark:text-green-400">
						<CircleCheck className="size-8" />
					</div>
					<h1 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
						{m.auth_device_approved_title()}
					</h1>
					<p className="text-sm text-muted-foreground">
						{m.auth_device_approved_body()}
					</p>
				</CenteredCard>
			</Shell>
		);
	}

	if (verify.isPending) {
		return (
			<Shell>
				<div className="flex min-h-screen items-center justify-center">
					<MotionLoader size="lg" text={m.auth_device_loading()} />
				</div>
			</Shell>
		);
	}

	if (verify.isError) {
		return (
			<Shell>
				<CenteredCard>
					<div className="mb-6 inline-flex size-16 items-center justify-center rounded-2xl bg-destructive/10 text-destructive ring-1 ring-destructive/20">
						<TriangleAlert className="size-8" />
					</div>
					<h1 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
						{m.auth_device_error_title()}
					</h1>
					<p className="mb-6 text-sm text-muted-foreground">
						{m.auth_device_error_generic()}
					</p>
					<Button onClick={onCancel} variant="outline" size="lg">
						{m.auth_device_cancel()}
					</Button>
				</CenteredCard>
			</Shell>
		);
	}

	const viewProps: DeviceApprovalViewProps = {
		status: approveMutation.isPending ? "approving" : "ready",
		clientName: verify.data.clientName ?? m.auth_device_generic_client(),
		userCode: canonicalCode,
		onApprove: () =>
			approveMutation.mutate(
				{ body: { code: canonicalCode } },
				{ onSuccess: () => setApproved(true) },
			),
		onCancel,
	};

	return (
		<Shell>
			<DeviceApprovalCard {...viewProps} />
		</Shell>
	);
}
